import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Eye } from "lucide-react";
import Heading from "@/components/tpr/Heading/Heading";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useHelpArticle } from "@/hooks/useHelpContent";

const HelpArticleView = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setBreadcrumb } = useBreadcrumb();
  const { data: article, isLoading, error } = useHelpArticle(slug!);

  useEffect(() => {
    if (article) {
      setBreadcrumb(
        <>
          <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
            <BreadcrumbLink href="/help">Help &amp; Support</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
            <BreadcrumbLink href="#">{article.title}</BreadcrumbLink>
          </BreadcrumbItem>
        </>,
      );
    }
    return () => setBreadcrumb(null);
  }, [setBreadcrumb, article]);

  if (isLoading) {
    return (
      <div className="px-4 min-h-[80vh]">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-32 mb-4" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-6 w-3/4 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="px-4 min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Article Not Found</h2>
          <p className="text-gray-600 mb-6">
            The help article you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/help")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Help
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 min-h-[80vh] pb-12">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 mt-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Article Header */}
        <div className="mb-8">
          <Heading title={article.title} />
          <p className="text-lg text-gray-600 mt-4">{article.summary}</p>

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {article.viewCount} {article.viewCount === 1 ? "view" : "views"}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Updated {new Date(article.updatedAt).toLocaleDateString()}
            </span>
          </div>

          {/* Tags */}
          {article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Video Embed */}
        {article.videoUrl && (
          <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
            <div className="relative pb-[56.25%]">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={article.videoUrl}
                title={article.title}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </div>
        )}

        {/* Article Content (Markdown) */}
        <div className="prose prose-lg max-w-none mb-8">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Customize markdown rendering
              h1: ({ node, ...props }) => <h1 className="text-3xl font-bold mt-8 mb-4" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-2xl font-semibold mt-6 mb-3" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-xl font-semibold mt-4 mb-2" {...props} />,
              p: ({ node, ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-4 space-y-2" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />,
              li: ({ node, ...props }) => <li className="ml-4" {...props} />,
              code: ({ node, inline, ...props }: any) =>
                inline ? (
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono" {...props} />
                ) : (
                  <code className="block bg-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto" {...props} />
                ),
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 my-4" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
              ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </div>

        {/* Images Gallery */}
        {article.imageUrls.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Screenshots & Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {article.imageUrls.map((url, i) => (
                <div key={i} className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src={url}
                    alt={`Screenshot ${i + 1}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            Was this article helpful? Let us know at support@thepracticerooms.com
          </p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpArticleView;
